import { getApplicationEndpoint } from '@gitbook/env';
import { createPalette, type PaletteCommandDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { clipboard } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { shortcuts } from '../../Keyboard';
import { type NavigationTarget, useNavigation } from '../../navigation';
import { copyTitleAsLink } from './copyTitleAsLink';

/**
 * Returns a function to create copy commands
 */
export function useCopyCommands(): (props: {
    target: NavigationTarget;
    id: string;
    title: string;
    onCopyLink?: () => void;
}) => PaletteCommandDef {
    const { getNavigationURL } = useNavigation();

    return useEventCallback((props) => {
        const url = `${getApplicationEndpoint()}${getNavigationURL(props.target)}`;
        return {
            key: 'copy',
            icon: Icon.Copy,
            title: 'Copy',
            descendant: () => {
                return createPalette({
                    static: true,
                    groups: [
                        {
                            key: 'copy',
                            commands: [
                                {
                                    key: 'copyLink',
                                    icon: Icon.Link,
                                    title: 'Copy URL',
                                    trigger: () => {
                                        props.onCopyLink?.();
                                        clipboard.writeText(url, {
                                            notify: { title: 'URL copied in clipboard' },
                                        });
                                    },
                                },
                                {
                                    key: 'copyId',
                                    icon: Icon.ID,
                                    title: 'Copy ID',
                                    trigger: () => {
                                        clipboard.writeText(props.id, {
                                            notify: { title: 'ID copied in clipboard' },
                                        });
                                    },
                                },
                                {
                                    key: 'copyTitle',
                                    icon: Icon.Type,
                                    title: 'Copy title',
                                    trigger: () => {
                                        clipboard.writeText(props.title, {
                                            notify: { title: 'Title copied in clipboard' },
                                        });
                                    },
                                },
                                {
                                    key: 'copyTitleAsLink',
                                    icon: Icon.Type,
                                    title: 'Copy title as link',
                                    keyboardShortcut: shortcuts.COPY_CHANGE_REQUEST_TITLE,
                                    trigger: () => {
                                        copyTitleAsLink(props.title, url);
                                    },
                                },
                            ],
                        },
                    ],
                });
            },
        };
    });
}
